// ============================================================
// DefenceProbabilityCarousel - 5-slide defence statistics.
// Slides: Dodge, Parry, Defence Chain, Damage After Defence,
//         Defence Survival.
// ============================================================
import { Fragment, type ReactNode } from 'react';
import styles from '../styles/DefenceProbabilityCarousel.module.css';
import type { DefenceProbabilityResult } from '../engine/defenceProbability';
import DodgeSuccessChart from './charts/DodgeSuccessChart';
import ParrySuccessChart from './charts/ParrySuccessChart';
import DefenceChainChart from './charts/DefenceChainChart';
import DamageAfterDefenceChart from './charts/DamageAfterDefenceChart';
import DefenceSurvivalChart from './charts/DefenceSurvivalChart';
import { ChartCarousel, StatBadge, carouselStatus } from './charts/ChartCarousel';

interface Props {
  result: DefenceProbabilityResult | null;
  loading?: boolean;
  hasAttackerContext: boolean;
}

const SLIDES = [
  { key: 'dodge', label: 'Dodge Probability' },
  { key: 'parry', label: 'Parry Probability' },
  { key: 'chain', label: 'Defence Chain' },
  { key: 'damage', label: 'Damage After Defence' },
  { key: 'survival', label: 'Defence Survival' },
] as const;

type SlideKey = (typeof SLIDES)[number]['key'];

/** The slides that need an attacker's weapon to mean anything. */
const NEEDS_WEAPON = new Set<SlideKey>(['chain', 'damage', 'survival']);

export default function DefenceProbabilityCarousel({ result, loading, hasAttackerContext }: Props) {
  /** A weapon-dependent slide shows only with an attacker's weapon in hand. */
  const shows = (slide: SlideKey) => hasAttackerContext || !NEEDS_WEAPON.has(slide);
  const slideContent = (slide: SlideKey) =>
    !shows(slide) ? (
      <div className={styles.placeholder}>Select a weapon to view full defence statistics.</div>
    ) : (
      <div className={styles.slide}>{result && <DefenceChart slide={slide} result={result} />}</div>
    );
  return (
    <ChartCarousel<SlideKey>
      slides={SLIDES}
      styles={styles}
      status={carouselStatus(result, loading)}
      emptyText="Select a target to view defence statistics."
      renderSlide={slideContent}
      renderStats={(slide) => result && shows(slide) && <DefenceStats slide={slide} result={result} />}
    />
  );
}

/** The slide's chart; an attack-dependent one draws nothing without its data. */
function DefenceChart({ slide, result }: { slide: SlideKey; result: DefenceProbabilityResult }) {
  if (slide === 'dodge') return <DodgeSuccessChart data={result.dodge} />;
  if (slide === 'parry') return <ParrySuccessChart data={result.parry} />;
  if (slide === 'chain') return result.chain && <DefenceChainChart data={result.chain} />;
  if (slide === 'damage') return result.damage && <DamageAfterDefenceChart data={result.damage} />;
  return result.survival && <DefenceSurvivalChart data={result.survival} />;
}

// Declarative: each slide's summary stats (an attack-dependent one only with its data).
function DefenceStats({ slide, result }: { slide: SlideKey; result: DefenceProbabilityResult }) {
  const { dodge, parry, chain, damage, survival } = result;
  const reaction = (label: string, r: typeof dodge) => (
    <>
      <StatBadge styles={styles} label={label}>{`${(r.successChance * 100).toFixed(0)}%`}</StatBadge>
      <StatBadge styles={styles} label="E[DoS]:">
        {r.expectedDoS.toFixed(1)}
      </StatBadge>
    </>
  );
  const stats: Record<SlideKey, ReactNode> = {
    dodge: reaction('Dodge:', dodge),
    parry: reaction('Parry:', parry),
    chain: chain && (
      <StatBadge styles={styles} label="E[avoided]:">
        {chain.expectedAvoided.toFixed(1)}
      </StatBadge>
    ),
    damage: damage && (
      <>
        <StatBadge styles={styles} label="Mean:">
          {damage.mean.toFixed(1)}
        </StatBadge>
        <StatBadge styles={styles} label="P95:">
          {damage.p95}
        </StatBadge>
      </>
    ),
    survival: survival && (
      <StatBadge styles={styles} label="Avg:">
        {survival.meanAttacks.toFixed(1)} attacks
      </StatBadge>
    ),
  };
  // Keyed by slide: each slide's badges are their own elements, never a neighbour's reused.
  return <Fragment key={slide}>{stats[slide]}</Fragment>;
}
